import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { Observable, ReplaySubject, Subscription, of } from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';
import { DcAlertService } from '../ui-kit/dc-alert/dc-alert.service';
import { ImportProductModalService } from './import-product/import-product-modal.service';
import { ProductSearchFilter } from './model/classes/product-search-filter';
import { ProductSearchForm } from './model/classes/product-search-form';
import { PRODUCT_TYPE } from './model/consts/product-type.conts';
import { CsvData } from './model/interfaces/csv-data.interface';
import { PageData } from './model/interfaces/page-data';
import {
  ProductCapturePagedData,
  ProductCaptureRow
} from './model/interfaces/product-capture.interface';
import { ProductCaptureService } from './services/product-capture.service';

@Component({
  selector: 'app-product-capture',
  templateUrl: './product-capture.component.html',
  styleUrls: ['./product-capture.component.scss'],
})
export class ProductCaptureComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('productCaptureList') productCaptureList: ElementRef;

  currencies: string[];
  currencySelect$: Observable<any[]>;
  loading = true;
  productTypesSelect = PRODUCT_TYPE;
  rows = [];
  tableFiltersForm: ProductSearchForm;
  csvData$: Observable<CsvData[]>;

  pageData: PageData = {
    fetchSize: 100, // The number of rows that is requested with each fetchData call
    displaySize: 22, // The number of rows that is displayed on the screen
    rowHeight: 40,
    totalElements: 0,
    fetchPageNumber: 0, // The offset that is requested with each fetchData call
    displayPageNumber: 0, // The page number that is applicable to the screen, taking into account the displaySize
    externalPaging: true,
    verticalScrolling: true,
  };

  private filtersChangeSubscription$: Subscription;
  private filterEvent$: ReplaySubject<ProductSearchFilter> =
    new ReplaySubject<ProductSearchFilter>(null);
  private filterEventSubscription$: Subscription;

  constructor(
    private productCaptureService: ProductCaptureService,
    private dcAlertService: DcAlertService,
    private importProductModalService: ImportProductModalService
  ) {}

  ngOnInit(): void {
    this.tableFiltersForm = new ProductSearchForm();

    this.filtersChangeSubscription$ =
      this.tableFiltersForm.valueChanges.subscribe(() =>
        this.filterEvent$.next(this.tableFiltersForm.toPayload())
      );

    this.currencySelect$ = this.productCaptureService.getCurrencies().pipe(
      take(1),
      map((currencies) =>
        currencies.map((currency) => ({
          id: currency.shortName,
          displayValue: currency.shortName,
        }))
      )
    );

    this.csvData$ = this.createCsvDataLoader();
  }

  ngAfterViewInit(): void {
    /* Give Angular time to fully resolve the view and allocate space to the table */
    this.fetchDataAndResetRowsAndPages(this.tableFiltersForm.toPayload());

    this.filterEventSubscription$ = this.filterEvent$
      .pipe(debounceTime(750))
      .subscribe((filter: ProductSearchFilter) => {
        this.tableFiltersForm.valid &&
          this.fetchDataAndResetRowsAndPages(filter);
      });
  }

  fetchDataAndResetRowsAndPages(
    searchFilter: ProductSearchFilter = null
  ): void {
    this.loading = true;
    this.pageData.displayPageNumber = 0;
    this.pageData.fetchPageNumber = 0;

    this.productCaptureService
      .getProductsPagedAndFiltered(
        this.pageData.fetchPageNumber,
        this.pageData.fetchSize,
        searchFilter
      )
      .pipe(take(1))
      .subscribe(
        (productCaptures: ProductCapturePagedData) => {
          this.pageData.totalElements = productCaptures.total;
          this.rows = productCaptures.records;
          this.loading = false;
        },
        (error) => {
          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Failed to load products: ${error.message}`,
          });
          this.loading = false;
        }
      );
  }

  fetchDataAndIncrementRowsAndPages(
    searchFilter: ProductSearchFilter = null
  ): void {
    this.pageData.fetchPageNumber++;

    this.productCaptureService
      .getProductsPagedAndFiltered(
        this.pageData.fetchPageNumber,
        this.pageData.fetchSize,
        searchFilter
      )
      .pipe(take(1))
      .subscribe(
        (productCaptures: ProductCapturePagedData) => {
          this.pageData.totalElements = productCaptures.total;
          this.rows = [...this.rows, ...productCaptures.records];
        },
        (error) => {
          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Failed to load products: ${error.message}`,
          });
          this.loading = false;
        }
      );
  }

  createCsvDataLoader(): Observable<any> {
    return of(true).
      pipe(
        map(() => this.tableFiltersForm.toPayload()),
        switchMap((searchFilter: ProductSearchFilter) =>
        this.productCaptureService
          .getProducts(searchFilter)
          .pipe(
            take(1),
            map((productCaptures: ProductCapturePagedData) => this.formatCsvData(productCaptures.records))
          )
        )
      );
  }

  clearSearch(): void {
    this.tableFiltersForm.controls.searchTerm.setValue('');
  }

  deleteRow(e: any): void {
    this.loading = true;
    this.productCaptureService.deleteProduct(e.id).subscribe(
      () => {
        this.dcAlertService.showAlert({
          type: 'success',
          message: `${e.name} successfully deleted.`,
        });
        this.fetchDataAndResetRowsAndPages(this.tableFiltersForm.toPayload());
      },
      (error) => {
        this.loading = false;

        this.dcAlertService.showAlert({
          type: 'danger',
          message: `Failed to delete product: ${error.message}`,
        });
        console.log(error);
      }
    );
  }

  formatCsvData(productCaptureRows: ProductCaptureRow[]): any[] {
    //Data for CSV file
    const dataArray = productCaptureRows.map((obj) => {
      const data = {
        'Created On': obj.createdOn,
        ISIN: obj.isin,
        'Product Name': obj.productName,
        productId: obj.productId,
        Tenor: obj.tenor,
        CCY: obj.currency,
        'CPN Freq': obj.frequency,
        Notional: obj.notional,
        'Initial OBS': obj.initialFixingDate,
        'Final OBS': obj.finalFixingDate,
        'Issue Date': obj.issueDate,
        'Product Type': obj.productType,
        Redemption: obj.maturityDate,
        Status: obj.recordStatus,
      };

      return data;
    });

    //Sorted from newest - oldest (from UTC Date Time string)
    let sortedData = dataArray.sort(
      (a: any, b: any) =>
        b['Created On'].replace(/[^0-9]/g, '') -
        a['Created On'].replace(/[^0-9]/g, '')
    );

    return sortedData;
  }

  openImportProductModal(): void {
    this.importProductModalService.openModal()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result === 'success') {
          this.dcAlertService.success('All products imported successfully.');
          this.fetchDataAndResetRowsAndPages(this.tableFiltersForm.toPayload());
        }
      });
  }

  pageChange(e: any): void {
    if (e.offset === this.pageData.displayPageNumber) {
      return;
    }

    this.pageData.displayPageNumber = e.offset;
    this.pageData.displaySize = e.pageSize;
    this.pageData.totalElements = e.count;

    const maxPageNumber = Math.ceil(
      this.rows?.length / this.pageData.displaySize
    );
    if (
      maxPageNumber - (1 + e.offset) <= 2 &&
      this.rows?.length < this.pageData.totalElements // Fetch two pages ahead if there are more records to fetch
    ) {
      this.fetchDataAndIncrementRowsAndPages(this.tableFiltersForm.toPayload());
    }
  }

  ngOnDestroy(): void {
    this.filtersChangeSubscription$?.unsubscribe();
    this.filterEventSubscription$?.unsubscribe();
  }
}
